/**
 * ============================================================================
 * ProgramCard2 — CollabOverlay (View)
 * ============================================================================
 * Purpose: Presentational collaboration logo strip for the card image.
 * ============================================================================
 */

"use client";

import { memo } from "react";
import { IMAGE_ASSETS } from "@/constants/images";
import UnivoImage from "@/components/common/UnivoImage";
import style from "../ProgramCard2.module.scss";
import type {
  CollabLogoDef,
  CollabLogoKey,
} from "../model/program-card2.types";

/** Stable style objects — avoid new `{}` each render. */
const RICS_OVERLAY_STYLE = { background: "#482F6A" } as const;
const RICS_LABEL_STYLE = { color: "#FFF" } as const;
const DEFAULT_COLLAB_LABEL = "In Collaboration with";
const COLLAB_LABEL_BY_INDUSTRY_ALIGNED_VALUE: Record<string, string> = {
  RICS: "In Association with",
  WOMEN: "Exclusive Cohort",
};

/** Static logo defs — created once at module load. */
const COLLAB_LOGOS: Record<CollabLogoKey, CollabLogoDef> = {
  meda: {
    src: IMAGE_ASSETS.medvarsityCollabLogo,
    className: style.medvarsity_img,
    alt: "medvarsity logo",
    width: 50,
    height: 20,
    sizes: "50px",
    unoptimized: true,
  },
  paytm: {
    src: IMAGE_ASSETS.paytmCollabLogo,
    className: style.paytm_img,
    alt: "paytm logo",
    width: 50,
    height: 20,
    sizes: "50px",
  },
  tcs: {
    src: IMAGE_ASSETS.tcsIonCollabLogo,
    className: style.tcs_img,
    alt: "TCS iON logo",
    width: 58,
    height: 24,
    sizes: "58px",
  },
  hcl: {
    src: IMAGE_ASSETS.hclCollabLogo,
    className: style.hcl_img,
    alt: "hcl logo",
    width: 100,
    height: 40,
    sizes: "100px",
  },
  kpmg: {
    src: IMAGE_ASSETS.kpmgCollabLogo,
    className: style.kpmg_img,
    alt: "KPMG logo",
    width: 58,
    height: 24,
    sizes: "58px",
  },
  soai: {
    src: IMAGE_ASSETS.soaiLogo,
    className: style.soai_img,
    alt: "soai logo",
    width: 100,
    height: 40,
    sizes: "100px",
    wrapperClassName: style.soai_img_container,
  },
  rics: {
    src: IMAGE_ASSETS.ricsCollabLogo,
    className: style.rics,
    alt: "RICS",
    width: 80,
    height: 20,
    sizes: "80px",
    unoptimized: true,
  },
  women: {
    src: IMAGE_ASSETS.womenCollabLogo,
    className: style.women_img,
    alt: "for women logo",
    width: 70,
    height: 40,
    sizes: "80px"
  },
  pmi: {
     src: IMAGE_ASSETS.pmiLogo,
    className: style.women_img,
    alt: "for women logo",
    width: 70,
    height: 40,
    sizes: "80px"
  }
};

const CollabLogo = memo(function CollabLogo({
  logoKey,
}: {
  logoKey: CollabLogoKey;
}) {
  const logo = COLLAB_LOGOS[logoKey];
  if (!logo) return null;

  const image = (
    <UnivoImage
      fetchPriority="low"
      loading="lazy"
      src={logo.src}
      className={logo.className}
      alt={logo.alt}
      width={logo.width}
      height={logo.height}
      sizes={logo.sizes}
      unoptimized={logo.unoptimized}
    />
  );

  if (logo.wrapperClassName) {
    return <div className={logo.wrapperClassName}>{image}</div>;
  }

  return image;
});

type CollabOverlayProps = {
  visibleCollabKeys: CollabLogoKey[];
  industryAlignedValue: string;
};

function CollabOverlayBase({
  visibleCollabKeys,
  industryAlignedValue
}: CollabOverlayProps) {
  if (visibleCollabKeys.length === 0) return null;

  return (
    <div
      className={style.collab_overlay}
      style={industryAlignedValue === "RICS" ? RICS_OVERLAY_STYLE : undefined}
    >
      {industryAlignedValue !== "PMI" && <span
        className={style.collab_label}
        style={industryAlignedValue === "RICS" ? RICS_LABEL_STYLE : undefined}
      >
        {COLLAB_LABEL_BY_INDUSTRY_ALIGNED_VALUE[industryAlignedValue] ??
          DEFAULT_COLLAB_LABEL}
      </span>}
      <div className={style.collab_logos}>
        {visibleCollabKeys.map((key) => (
          <CollabLogo key={key} logoKey={key} />
        ))}
      </div>
    </div>
  );
}

const CollabOverlay = memo(CollabOverlayBase);
CollabOverlay.displayName = "CollabOverlay";

export default CollabOverlay;
